import type { Filters } from '@/components/audit/FilterBar'
import { api } from '@/lib/api'

/** Status → semantic colour (CSS value), shared by the log, run page and insights. */
export function statusColor(s: string): string {
  if (s === 'success') return 'var(--ok)'
  if (s === 'partial' || s === 'awaiting_confirm' || s === 'preview') return 'var(--warn)'
  if (s === 'failed' || s === 'unknown' || s === 'interrupted') return 'var(--danger)'
  return 'var(--text-2)'
}

/** Status → Tailwind text class (the word always carries the meaning, colour only repeats it). */
export function statusTone(s: string): string {
  if (s === 'success') return 'text-ok'
  if (s === 'partial' || s === 'awaiting_confirm' || s === 'preview') return 'text-warn'
  if (s === 'failed' || s === 'unknown' || s === 'interrupted') return 'text-danger'
  return 'text-text-2'
}

/** Run duration in ms, or null while the run is open. */
export function durationMs(started: string, finished: string | null): number | null {
  if (!finished) return null
  const d = new Date(finished).getTime() - new Date(started).getTime()
  return Number.isFinite(d) && d >= 0 ? d : null
}

/** 896 → "896 ms", 30858 → "30.9 s" */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return '—'
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`
}

/** "18.10 14:02" — compact local time for table rows */
export function fmtWhen(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Filter params shared by the list fetch, CSV export, and stats (no pagination). */
export function buildAuditParams(f: Filters): URLSearchParams {
  const p = new URLSearchParams()
  p.set('order', f.order)
  if (f.q) p.set('q', f.q)
  if (f.status) p.set('status', f.status)
  if (f.surface) p.set('surface', f.surface)
  if (f.critical) p.set('critical', 'true')
  if (f.silentFailure) p.set('silent_failure', 'true')
  if (f.dryRun) p.set('dry_run', 'true')
  if (f.since) p.set('since', `${f.since}T00:00:00`)
  if (f.until) p.set('until', `${f.until}T23:59:59`)
  return p
}

export type AuditStats = {
  window: { since: string | null; until: string | null; scanned: number; truncated: boolean }
  totals: {
    total: number
    by_status: Record<string, number>
    by_surface: Record<string, number>
    dry_run: number
    real: number
    critical: number
    silent_failure: number
  }
  latency_ms: { count: number; avg: number; p50: number; p95: number; max: number } | null
  top_operations: {
    operation: string
    count: number
    success: number
    failed: number
    avg_latency_ms: number | null
  }[]
}

export function fetchAuditStats(f: Filters): Promise<AuditStats> {
  const qs = buildAuditParams(f).toString()
  return api<AuditStats>(`/audit/stats${qs ? `?${qs}` : ''}`)
}
